import React, { useCallback } from "react";
import { Counterparty } from "@common/types/counterparty";
import { CounterpartyCombobox } from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { BaseCellProps } from "@components/spreadsheet/cells/index";
import styles from "./CounterpartyCell.module.scss";
import { stopPropagation, stopPropagationExceptTab } from "./cellEditorEvents";

interface CounterpartyCellProps extends BaseCellProps<string> {
    counterpartiesMap?: Map<string, string>;
}

export const CounterpartyCell: React.FC<CounterpartyCellProps> = ({
    value,
    isEditing,
    finishEditing,
    enterEditModeKey,
    counterpartiesMap,
}) => {
    const resolvedName = value ? counterpartiesMap?.get(value) : undefined;

    const selectedCounterparty = value
        ? { id: value, name: resolvedName ?? "" }
        : undefined;

    const handleChange = useCallback(
        (counterparty?: Pick<Counterparty, "id" | "name">) => {
            finishEditing(counterparty?.id ?? null);
        },
        [finishEditing],
    );

    if (isEditing) {
        return (
            <>
                <div
                    className={styles.counterpartyCellEditor}
                    onClick={stopPropagation}
                    onMouseDown={stopPropagation}
                    onKeyDown={stopPropagationExceptTab}
                >
                    <CounterpartyCombobox
                        selectedCounterparty={selectedCounterparty}
                        onChange={handleChange}
                        cellEditor={{ openingKey: enterEditModeKey }}
                        initialOpen
                        hideArrow
                        triggerButtonProps={{
                            block: true,
                            variant: "primary",
                        }}
                    />
                </div>
                <CounterpartyNameDisplay name={resolvedName} />
            </>
        );
    }

    return <CounterpartyNameDisplay name={resolvedName} />;
};

const CounterpartyNameDisplay: React.FC<{ name?: string }> = ({ name }) => {
    if (!name) {
        return null;
    }

    return (
        <BreakableText
            text={name}
            showTooltip
            className={styles.counterpartyName}
        />
    );
};
